import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface DriverLocation {
  lat: number;
  lng: number;
  accuracy?: number;
  speed?: number;
  heading?: number;
}

interface UseDriverLocationReturn {
  isOnline: boolean;
  setIsOnline: (online: boolean) => void;
  currentLocation: DriverLocation | null;
  isTracking: boolean;
  error: string | null;
  startTracking: () => void;
  stopTracking: () => void;
  updateLocationManually: (lat: number, lng: number) => Promise<void>;
}

export const useDriverLocation = (): UseDriverLocationReturn => {
  const { user } = useAuth();
  const [isOnline, setIsOnlineState] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<DriverLocation | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update driver status in database
  const updateDriverStatus = useCallback(async (online: boolean) => {
    if (!user?.id) return;

    try {
      const { error: updateError } = await supabase
        .from("driver_profiles")
        .update({ 
          is_online: online,
          updated_at: new Date().toISOString()
        })
        .eq("user_id", user.id);

      if (updateError) {
        console.error("Error updating driver status:", updateError);
        throw updateError;
      }
    } catch (err) {
      console.error("Failed to update driver status:", err);
      setError("Failed to update online status");
    }
  }, [user?.id]);

  // Update driver location in database
  const updateLocationInDB = useCallback(async (location: DriverLocation) => {
    if (!user?.id) return;

    try {
      const { error: updateError } = await supabase
        .from("driver_profiles")
        .update({ 
          current_lat: location.lat,
          current_lng: location.lng,
          updated_at: new Date().toISOString()
        })
        .eq("user_id", user.id);

      if (updateError) {
        console.error("Error updating location:", updateError);
      }
    } catch (err) {
      console.error("Failed to update location:", err);
    }
  }, [user?.id]);

  // Ref to store latest location for interval updates
  const latestLocationRef = useRef<DriverLocation | null>(null);

  // Start GPS tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      toast.error("GPS not supported on this device");
      return;
    }

    // Clear any existing watchers first
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }

    setIsTracking(true);
    setError(null);

    const startWatch = () => {
      // Watch position with high accuracy
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation: DriverLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            speed: position.coords.speed ?? undefined,
            heading: position.coords.heading ?? undefined,
          };

          setCurrentLocation(newLocation);
          latestLocationRef.current = newLocation;
          setError(null);

          // Immediately update location in DB on first position
          updateLocationInDB(newLocation);
        },
        (err) => {
          console.error("Geolocation error code:", err.code, "message:", err.message);

          let errorMessage = "Unknown GPS error";

          switch (err.code) {
            case 1: // PERMISSION_DENIED
              errorMessage = "Location permission denied. Please enable in browser/device settings.";
              toast.error("Location permission denied", {
                description: "Your browser will not re-ask after Deny. Change it to Allow in Settings, then retry.",
              });
              // When denied, clear stale location so UI doesn't look "active"
              setCurrentLocation(null);
              latestLocationRef.current = null;
              setIsTracking(false);
              break;
            case 2: // POSITION_UNAVAILABLE
              errorMessage = "GPS signal unavailable. Try moving to an open area.";
              toast.error("GPS signal unavailable", {
                description: "Try moving outdoors or near a window",
              });
              break;
            case 3: // TIMEOUT
              errorMessage = "GPS request timed out. Retrying...";
              toast.warning("GPS taking too long", {
                description: "Retrying location request...",
              });
              // Auto-retry on timeout
              setTimeout(() => startTracking(), 2000);
              break;
          }

          setError(errorMessage);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0, // Always get fresh position
        }
      );

      // Update database every 5 seconds using ref to get latest location
      updateIntervalRef.current = setInterval(() => {
        if (latestLocationRef.current) {
          updateLocationInDB(latestLocationRef.current);
        }
      }, 5000);
    };

    // iOS Safari sometimes won't show a permission prompt reliably for watchPosition.
    // Calling getCurrentPosition first helps trigger the permission prompt.
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation: DriverLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed ?? undefined,
          heading: position.coords.heading ?? undefined,
        };

        setCurrentLocation(newLocation);
        latestLocationRef.current = newLocation;
        setError(null);
        updateLocationInDB(newLocation);

        startWatch();
      },
      () => {
        // If this fails (permission/timeout/etc), watchPosition will still report a more detailed error.
        startWatch();
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  }, [updateLocationInDB]);

  // Stop GPS tracking
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }

    setIsTracking(false);
  }, []);

  // Handle going online/offline
  const setIsOnline = useCallback(async (online: boolean) => {
    if (!user?.id) return;

    // Check if driver is approved before allowing online status
    if (online) {
      const { data: profile, error: profileError } = await supabase
        .from("driver_profiles")
        .select("status, documents_verified, vehicle_verified")
        .eq("user_id", user.id)
        .maybeSingle();

      if (profileError || !profile) {
        toast.error("Could not verify driver status");
        return;
      }

      // Only allow online if status is 'approved' or 'active'
      if (profile.status !== 'approved' && profile.status !== 'active') {
        toast.error("Account not approved", {
          description: "Your account must be approved by admin before you can go online. Current status: " + (profile.status || 'pending')
        });
        return;
      }

      // Check permission state first (if Permissions API is available)
      if (navigator.permissions) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
          
          if (permissionStatus.state === "denied") {
            toast.error("Location access blocked", {
              description: "Please enable location in your browser/device settings, then try again.",
              duration: 6000,
            });
            setError("Location permission denied. Please enable in browser/device settings.");
            return; // Don't go online if permission is denied
          }
        } catch {
          // Permissions API not supported (e.g., older Safari), continue anyway
        }
      }
    }

    setIsOnlineState(online);
    await updateDriverStatus(online);
    
    if (online) {
      // Start tracking immediately when going online - this triggers the GPS permission prompt
      toast.info("Requesting GPS access...", {
        description: "Please tap 'Allow' when prompted for location access"
      });
      
      // Small delay to show the toast before the permission prompt appears
      setTimeout(() => {
        startTracking();
      }, 300);
    } else {
      stopTracking();
      toast.info("You're now offline", {
        description: "GPS tracking stopped"
      });
    }
  }, [user?.id, updateDriverStatus, startTracking, stopTracking]);

  // Manual location update
  const updateLocationManually = useCallback(async (lat: number, lng: number) => {
    const location: DriverLocation = { lat, lng };
    setCurrentLocation(location);
    await updateLocationInDB(location);
  }, [updateLocationInDB]);

  // Update location in DB when it changes
  useEffect(() => {
    if (isOnline && currentLocation) {
      updateLocationInDB(currentLocation);
    }
  }, [currentLocation, isOnline, updateLocationInDB]);

  // Load initial online status from database (and ensure a row exists)
  useEffect(() => {
    const loadInitialStatus = async () => {
      if (!user?.id) return;

      const { data, error: readError } = await supabase
        .from("driver_profiles")
        .select("is_online, current_lat, current_lng, status")
        .eq("user_id", user.id)
        .maybeSingle();

      // If the driver profile row doesn't exist yet, create it with offline status
      if (!data && !readError) {
        await supabase.from("driver_profiles").upsert({
          user_id: user.id,
          is_online: false,
          status: 'pending',
          updated_at: new Date().toISOString(),
        });
        setIsOnlineState(false);
        return;
      }

      if (data) {
        // Only allow online if driver is approved
        const isApproved = data.status === 'approved' || data.status === 'active';
        const initialOnline = isApproved && !!data.is_online;
        setIsOnlineState(initialOnline);

        if (data.current_lat && data.current_lng) {
          setCurrentLocation({ lat: data.current_lat, lng: data.current_lng });
        }

        // Only start tracking if approved and was online
        if (initialOnline) {
          startTracking();
        }
      }
    };

    loadInitialStatus();

    return () => {
      stopTracking();
    };
  }, [user?.id, startTracking, stopTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  return {
    isOnline,
    setIsOnline,
    currentLocation,
    isTracking,
    error,
    startTracking,
    stopTracking,
    updateLocationManually,
  };
};
